import { type ApiShift, type ApiUser, useShifts, useUsers } from '@/api/data';
import { useUserWrites } from '@/api/people';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
/**
 * Ansatte (manager redesign, 2026-10-09; prototype proto-Ansatte): the roster with employee number,
 * agreed week, planned hours this week and the Oppsett column, the thing the old list did not
 * say (plan 0019). Selecting a row shows a short context beside the list; the name and
 * "Åpne profil" open the profile. Every setup tag opens the profile's editor on that field.
 */
import { T } from '@/i18n';
import { oslo, plannedHours, today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { PersonLabel } from '@/ui/PersonLabel';
import { avTone, fmtDs, fmtPhone, fullName, initials, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { DetailPanel, Fact, PageHead, Status } from '@/ui/page';
import { addDays, isoWeek, mondayOf } from '@timefly/core';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './ansatt/ansatte.css';
import { likelyDomain } from './ansatt/emailTypo';
import { AppSheet, InviteState, LinkDrawer, bulkTargets, invitation } from './ansatt/invite';
import { useAccountMail } from './ansatt/mail';
import {
  type Gap,
  endNotice,
  matches,
  roster,
  setupItems,
  stillingsprosent,
  weekMissing,
  whoOf,
} from './ansatt/roster';
import { nextShift, onDuty } from './ansatt/summary';

const clock = (iso: string | null | undefined) => oslo(iso)?.time?.slice(0, 5) ?? '';

/** The agreed week as stored: unknown is "Ikke satt", never 0 or an invented default. */
function Agreed({ u }: { u: ApiUser }) {
  const { tenant } = useTenant();
  const w = u.max_hours_per_week;
  if (w == null) return <span className="ans-warn">{T('Ikke satt')}</span>;
  if (w <= 0) return <span className="ans-warn">{T('Ugyldig: {n} t', { n: num(w) })}</span>;
  // stillingsprosent (#108): the week over the company's, 37,5 where the company has set none
  const pct = stillingsprosent(
    u,
    tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : 37.5,
  );
  return (
    <>
      {num(w)} t{pct != null && ` · ${pct} %`}
      {/* own week null: the company's week applies (plan 0027 B) */}
      {u.agreed_week_hours === null && <small>{T('Bedriftens uke')}</small>}
    </>
  );
}

/** The end date of a temporary job (#108): said 30 days before it, and after it has passed. */
export function EndNotice({ u, today: day }: { u: Pick<ApiUser, 'end_date'>; today: string }) {
  const n = endNotice(u, day);
  if (!n) return null;
  return (
    <Status tone={n.kind === 'passed' ? 'dgr' : 'warn'}>
      {n.kind === 'passed' ? T('Sluttet {d}', { d: fmtDs(n.date) }) : T('Slutter {d}', { d: fmtDs(n.date) })}
    </Status>
  );
}

const gapLink = (u: ApiUser, g: Gap) => (
  <Link
    key={g.field}
    to={`/ansatte/${u.id}?rediger=${g.field}`}
    className="ans-gap"
    aria-label={T('{gap}, {name}: legg inn', { gap: T(g.text), name: fullName(u) })}
    onClick={(e) => e.stopPropagation()}
  >
    {T(g.tag)}
  </Link>
);

export default function Ansatte() {
  const can = useCan();
  const nav = useNavigate();
  const monday = mondayOf(today());
  // "Labai reikia search funkcijos": with a long roster, finding one person is slow (product
  // user, 2026-09-21). Name, username, e-mail, employee number, team or job title, accents folded.
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<number | null>(null);
  // The status and the setup filter live in the address, so Back and a link restore them. Hjem and
  // Lønn link ?mangler=1, Oversikt → Oppfølging links ?oppsett=mangler; both open the setup list.
  const [sp, setSp] = useSearchParams();
  const { tenant } = useTenant();
  const onlyMissing = sp.get('oppsett') === 'mangler' || sp.get('mangler') === '1';
  // ?oppsett=uke (#54): only those with no agreed week, from the count line and Lønn's Kontroller
  const onlyWeek = sp.get('oppsett') === 'uke';
  // ?app=ikke: people who have never logged in, each with «Send invitasjon» (the welcome overview, #130).
  // Only the administrator's read of /users carries last_login_at, so only the administrator is offered it.
  const appKnown = tenant?.me?.is_admin === true;
  const notInApp = appKnown && sp.get('app') === 'ikke';
  const mail = useAccountMail();
  const writes = useUserWrites();
  const meId = useAuth((s) => s.user?.id);
  const [linkFor, setLinkFor] = useState<ApiUser | null>(null);
  const [sheet, setSheet] = useState(false);
  const who = whoOf(sp.get('vis'));
  const setParam = (k: 'vis' | 'oppsett' | 'app', v: string | null) => {
    const n = new URLSearchParams(sp);
    n.delete('mangler');
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n);
  };
  const usersQ = useUsers();
  // this week for the planned hours, two for the next shift in the quick view
  const shiftsQ = useShifts(monday, addDays(monday, 13));
  const shifts = shiftsQ.data?.shifts ?? [];
  // only the administrator receives the fields the setup summary and the agreed hours read
  const setupVisible = can('ansatte.endre');
  const all = roster(usersQ.data ?? [], who);
  const rows = all
    .filter((u) => matches(u, q))
    .map((u) => {
      const h = shifts
        .filter((s) => s.user_id === u.id && (oslo(s.start_time)?.date ?? '') <= addDays(monday, 6))
        .reduce((a, s) => a + plannedHours(s), 0);
      return { u, h, miss: setupItems(u, setupVisible) };
    })
    .filter((r) => !onlyMissing || r.miss.length > 0)
    .filter((r) => !onlyWeek || (setupVisible && weekMissing(r.u)))
    .filter((r) => !notInApp || r.u.last_login_at == null);
  const nMiss = all.filter((u) => setupItems(u, setupVisible).length).length;
  const nWeek = setupVisible ? all.filter(weekMissing).length : 0;
  const picked = rows.find((r) => r.u.id === sel) ?? null;
  // this company's teams only: the list carries every membership's teams for some readers
  const teamsOf = (u: ApiUser) =>
    (u.teams ?? [])
      .filter((t) => {
        const tid = (t as { tenant_id?: number }).tenant_id;
        return tid == null || tid === tenant?.id;
      })
      .map((t) => t.name)
      .filter(Boolean);
  const week = isoWeek(monday);
  // «Send til alle» (#120): the count is the server's rule, so the button says how many get a mail
  const fresh = bulkTargets(all, meId, false);
  const stale = bulkTargets(all, meId, true);
  const sendAll = async (again: boolean) => {
    const n = again ? stale.length : fresh.length;
    const ask = again
      ? T('Sende invitasjonen på nytt til {n} som ikke har logget inn?', { n })
      : T('Sende invitasjon på e-post til {n} som ikke er invitert?', { n });
    if (!window.confirm(ask)) return;
    try {
      const r = await writes.inviteAll.mutateAsync(again);
      toast(
        [
          T('Sendt til {n}.', { n: r.sent }),
          r.failed ? T('{n} ble ikke sendt.', { n: r.failed }) : '',
          r.without_email ? T('{n} uten e-post: bruk «Vis lenke».', { n: r.without_email }) : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (e) {
      toast(`${T('Ikke sendt')}: ${(e as Error).message}`);
    }
  };
  return (
    <div className="ans-page">
      <PageHead
        title={T('Ansatte')}
        sub={T('Kontakt, arbeidsforhold og det som må være på plass før planlegging og lønn.')}
        actions={
          can('ansatte.endre') && (
            <Button tone="pri" onClick={() => nav('/ansatte/ny')}>
              <Icon name="userPlus" />
              {T('Ny ansatt')}
            </Button>
          )
        }
      />
      <div className="mbar ans-bar">
        <input
          className="inp ans-q"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={T('Søk navn, e-post, ansattnr, team eller stilling')}
          aria-label={T('Søk i ansatte')}
          data-testid="ansatte-sok"
        />
        <label className="mf">
          {T('Ansatte')}
          <select
            className="inp"
            value={who}
            data-testid="ansatte-vis"
            onChange={(e) => setParam('vis', e.target.value === 'aktive' ? null : e.target.value)}
          >
            <option value="aktive">{T('Aktive')}</option>
            <option value="inaktive">{T('Inaktive')}</option>
            <option value="alle">{T('Alle')}</option>
          </select>
        </label>
        <span className="sp" />
        {setupVisible && (
          <label className="chk ans-miss">
            <input
              type="checkbox"
              checked={onlyMissing}
              data-testid="ansatte-mangler"
              onChange={(e) => setParam('oppsett', e.target.checked ? 'mangler' : null)}
            />
            {T('Bare de som mangler oppsett')}
            <span className="ans-n" data-testid="ansatte-mangler-n">
              {nMiss}
            </span>
          </label>
        )}
        {appKnown && (
          <label className="chk ans-miss">
            <input
              type="checkbox"
              checked={notInApp}
              data-testid="ansatte-ikke-innlogget"
              onChange={(e) => setParam('app', e.target.checked ? 'ikke' : null)}
            />
            {T('Bare de som ikke har logget inn')}
          </label>
        )}
      </div>
      {appKnown && (
        <div className="mbar ans-bar ans-inv" data-testid="ansatte-invitasjoner">
          {fresh.length > 0 && (
            <Button small tone="pri" data-testid="ansatte-inviter-alle" onClick={() => void sendAll(false)}>
              {T('Send til alle som ikke er invitert ({n})', { n: fresh.length })}
            </Button>
          )}
          {stale.length > 0 && (
            <Button small data-testid="ansatte-inviter-igjen" onClick={() => void sendAll(true)}>
              {T('Send på nytt til de som ikke har logget inn ({n})', { n: stale.length })}
            </Button>
          )}
          <span className="sp" />
          <Button small data-testid="ansatte-app-ark" onClick={() => setSheet(true)}>
            {T('Appen på papir')}
          </Button>
        </div>
      )}
      {nWeek > 0 && (
        <p className="ans-weekline" data-testid="ansatte-uke-mangler">
          {nWeek === 1
            ? T('1 ansatt mangler avtalt arbeidstid per uke.')
            : T('{n} ansatte mangler avtalt arbeidstid per uke.', { n: nWeek })}{' '}
          {T('Reglene bruker bedriftens uke, eller 37,5 t, til den er satt.')}{' '}
          <button
            type="button"
            className="lnk"
            data-testid="ansatte-uke-filter"
            onClick={() => setParam('oppsett', onlyWeek ? null : 'uke')}
          >
            {onlyWeek ? T('Vis alle') : T('Vis dem')}
          </button>
        </p>
      )}
      <div className={`msplit${picked ? ' open' : ''}`}>
        <section className="mcard">
          <div className="mch">
            <h2 data-testid="ansatte-n">
              {rows.length === 1 ? T('1 ansatt') : T('{n} ansatte', { n: rows.length })}
            </h2>
            {rows.length !== all.length && <span className="sub">{T('av {n}', { n: all.length })}</span>}
          </div>
          {rows.length === 0 ? (
            <p className="ans-empty">{usersQ.isLoading ? T('Henter…') : T('Ingen ansatte passer søket.')}</p>
          ) : (
            <table className="mt cards" data-testid="ansatte">
              <thead>
                <tr>
                  <th>{T('Ansatt')}</th>
                  <th>{T('Ansattnr')}</th>
                  {setupVisible && <th className="num">{T('Avtalt per uke')}</th>}
                  <th className="num">{T('Planlagt uke {n}', { n: week })}</th>
                  {setupVisible && <th>{T('Oppsett')}</th>}
                  {appKnown && <th>{T('Innlogging')}</th>}
                  <th>{T('Status')}</th>
                  <th>
                    <span className="sr">{T('Profil')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: the row click is a pointer shortcut; the keyboard path is the "Vis kort" button in the first cell
                  <tr
                    key={r.u.id}
                    className={`ans-row${sel === r.u.id ? ' sel' : ''}`}
                    data-testid="person-row"
                    onClick={() => setSel(sel === r.u.id ? null : r.u.id)}
                  >
                    <td>
                      <div className="mwho">
                        <span className={`av ${avTone(r.u.id)}`}>{initials(fullName(r.u))}</span>
                        <span>
                          <Link
                            to={`/ansatte/${r.u.id}`}
                            className="ans-name"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {fullName(r.u)}
                          </Link>
                          <small>{teamsOf(r.u).join(', ') || T('Ingen team')}</small>
                          <button
                            type="button"
                            className="ans-peek"
                            aria-pressed={sel === r.u.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSel(sel === r.u.id ? null : r.u.id);
                            }}
                          >
                            {T('Vis kort om {name}', { name: fullName(r.u) })}
                          </button>
                        </span>
                      </div>
                    </td>
                    {/* the number, not the job title: no route writes users.title, so a Stilling column
                        read "Ikke oppgitt" for everyone (#87). A title set some other way shows under it. */}
                    <td data-k={T('Ansattnr')}>
                      {r.u.employee_id || <span className="ans-mute">{T('Ansattnummer mangler')}</span>}
                      {r.u.title && <small className="ans-sub">{r.u.title}</small>}
                    </td>
                    {setupVisible && (
                      <td className="num" data-k={T('Avtalt')}>
                        <Agreed u={r.u} />
                      </td>
                    )}
                    <td className="num" data-k={T('Planlagt')}>
                      {num(r.h, 1)} t
                    </td>
                    {setupVisible && (
                      <td className="ans-setup">
                        {r.miss.length ? (
                          <span className="ans-gaps">{r.miss.map((g) => gapLink(r.u, g))}</span>
                        ) : (
                          <Status tone="ok">{T('Komplett')}</Status>
                        )}
                      </td>
                    )}
                    {appKnown && (
                      <td className="ans-inv" data-k={T('Innlogging')} data-testid="ansatte-innlogging">
                        <InviteState u={r.u} />
                        {r.u.email && likelyDomain(r.u.email) && (
                          // the address may be a typo (the hotel's «outloock.com»); a warning, never a block
                          <Link
                            to={`/ansatte/${r.u.id}?rediger=email`}
                            className="ans-gap"
                            data-testid="ansatte-epost-sjekk"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {T('Mente du {d}?', { d: likelyDomain(r.u.email) ?? '' })}
                          </Link>
                        )}
                        {r.u.active_status && invitation(r.u).kind !== 'in' && (
                          <span className="ans-invbtns">
                            {r.u.email && (
                              <Button
                                small
                                data-testid="ansatte-inviter"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void mail(r.u, 'welcome');
                                }}
                                aria-label={T('Send invitasjon til {name}', { name: fullName(r.u) })}
                              >
                                {r.u.invited_at ? T('Send på nytt') : T('Send invitasjon')}
                              </Button>
                            )}
                            <Button
                              small
                              data-testid="ansatte-vis-lenke"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLinkFor(r.u);
                              }}
                              aria-label={T('Vis invitasjonslenke for {name}', { name: fullName(r.u) })}
                            >
                              {T('Vis lenke')}
                            </Button>
                          </span>
                        )}
                      </td>
                    )}
                    <td>
                      {r.u.active_status ? (
                        <Status tone="ok">{T('Aktiv')}</Status>
                      ) : (
                        <Status tone="neu">{T('Inaktiv')}</Status>
                      )}
                      {setupVisible && <EndNotice u={r.u} today={today()} />}
                    </td>
                    <td className="ans-open">
                      <Link
                        to={`/ansatte/${r.u.id}`}
                        className="mlink"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={T('Åpne profilen til {name}', { name: fullName(r.u) })}
                      >
                        {T('Åpne profil')} <span aria-hidden="true">→</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        {picked && (
          <Peek
            u={picked.u}
            gaps={picked.miss}
            teams={teamsOf(picked.u)}
            mine={shifts.filter((s) => s.user_id === picked.u.id)}
            seeLoenn={can('ansatte.loenn')}
            setupVisible={setupVisible}
            onClose={() => setSel(null)}
          />
        )}
      </div>
      {appKnown && (
        <>
          <LinkDrawer u={linkFor} onClose={() => setLinkFor(null)} />
          <AppSheet open={sheet} onClose={() => setSheet(false)} />
        </>
      )}
      <p className="mfoot">{T('Tilgangsrollen gjelder den valgte bedriften og vises i profilen.')}</p>
    </div>
  );
}

/**
 * The quick context for one person, from what the list and the shift route already carry. No
 * messaging: Timefly has no message service on the web, so the contact is the phone and mail
 * links the device already has.
 */
function Peek({
  u,
  gaps,
  teams,
  mine,
  seeLoenn,
  setupVisible,
  onClose,
}: {
  u: ApiUser;
  gaps: Gap[];
  teams: string[];
  mine: ApiShift[];
  seeLoenn: boolean;
  setupVisible: boolean;
  onClose: () => void;
}) {
  const now = Date.now();
  const duty = onDuty(mine, now);
  const next = duty ?? nextShift(mine, now);
  // the list carries the balance to a reader with the personnel record only
  const left = (u as { vacation_days_balance?: number | null }).vacation_days_balance;
  return (
    <DetailPanel
      title={<PersonLabel id={u.id} name={fullName(u)} />}
      sub={[u.title, teams.join(', ')].filter(Boolean).join(' · ') || undefined}
      label={T('Kort om {name}', { name: fullName(u) })}
      onClose={onClose}
      footer={
        <>
          <Link className="btn pri" to={`/ansatte/${u.id}`}>
            <Icon name="user" />
            {T('Åpne profil')}
          </Link>
          {u.phone && (
            <a className="btn" href={`tel:${u.phone.replace(/\s/g, '')}`}>
              {T('Ring')}
            </a>
          )}
          {u.email && (
            <a className="btn" href={`mailto:${u.email}`}>
              {T('Send e-post')}
            </a>
          )}
        </>
      }
    >
      {setupVisible && (
        <Fact k={T('Avtalt arbeidstid')}>
          <Agreed u={u} />
        </Fact>
      )}
      <Fact k={T('Telefon')}>{u.phone ? fmtPhone(u.phone) : T('Ikke oppgitt')}</Fact>
      <Fact k={T('E-post')}>{u.email || T('Ingen e-post ennå')}</Fact>
      {typeof left === 'number' && <Fact k={T('Ferie igjen')}>{T('{n} dager', { n: num(left) })}</Fact>}
      {seeLoenn && (
        <Fact k={T('Lønnstype')}>{u.contract_type === 'monthly' ? T('Fastlønn') : T('Timelønn')}</Fact>
      )}
      <Fact k={duty ? T('Pågående vakt') : T('Neste vakt')}>
        {next
          ? `${fmtDs(oslo(next.start_time)?.date ?? '')} · ${clock(next.start_time)}–${clock(next.end_time)}`
          : T('Ingen de neste to ukene')}
      </Fact>
      {gaps.length > 0 && (
        <div className="ans-peekgaps">
          <span>{T('Mangler oppsett')}</span>
          <span className="ans-gaps">{gaps.map((g) => gapLink(u, g))}</span>
        </div>
      )}
    </DetailPanel>
  );
}
